import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { AverageStat } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ChevronDownBold } from '@sorare/core/src/atoms/icons/ChevronDownBold';
import { Collapse } from '@sorare/core/src/atoms/layout/Collapse';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { Switch } from '@sorare/core/src/components/search/Switch';
import { StatIcon } from '@sorare/core/src/components/stats/StatIcon';
import { glossary, scoutingCenter } from '@sorare/core/src/lib/glossary';
import type { Stats } from '@sorare/core/src/lib/scoring';
import { statLabels } from '@sorare/core/src/lib/scoring';

import { DECISIVE_ACTIONS, STATISTICS } from '#src/lib/scoutingCenter.ts';

const FootballStat = (stat: AverageStat) => ({
  value: stat,
  icon: <StatIcon statKey={stat.toLowerCase() as Stats} />,
  label:
    (statLabels[stat.toLowerCase() as keyof typeof statLabels] && (
      <FormattedMessage
        {...statLabels[stat.toLowerCase() as keyof typeof statLabels]}
      />
    )) ||
    stat.toLowerCase(),
});

const statistics = STATISTICS.map(FootballStat);
const decisiveActions = DECISIVE_ACTIONS.map(FootballStat);

const CapitalizeFirstLetter = styled.span`
  &::first-letter {
    text-transform: uppercase;
  }
`;

const StyledChevronDownBold = styled(ChevronDownBold)`
  transition: transform 0.3s ease-in-out;
  margin-right: var(--unit);
  &.open {
    transform: rotate(180deg);
  }
`;

type Props = {
  stats: AverageStat[];
  onChange: (sort: AverageStat[]) => void;
};

const MAIN_DECISIVE_ACTIONS_COUNT = 5;

export const FootballStats = ({ stats, onChange }: Props) => {
  const [showMoreDecisive, setShowMoreDecisive] = useState(false);
  const mainDecisiveActions = decisiveActions.slice(
    0,
    MAIN_DECISIVE_ACTIONS_COUNT
  );
  const otherDecisiveActions = decisiveActions.slice(
    MAIN_DECISIVE_ACTIONS_COUNT
  );
  return (
    <Vertical>
      <LabelM color="var(--c-nd-600)">
        <FormattedMessage {...scoutingCenter.statistics} />
      </LabelM>
      {statistics.map(({ value, icon, label }) => (
        <Switch
          key={value}
          label={
            <Horizontal>
              {icon} <CapitalizeFirstLetter>{label}</CapitalizeFirstLetter>
            </Horizontal>
          }
          checked={stats.some(v => v === value)}
          onChange={() =>
            onChange(
              stats.includes(value)
                ? stats.filter(v => v !== value)
                : [...stats, value]
            )
          }
        />
      ))}
      <LabelM color="var(--c-nd-600)">
        <FormattedMessage {...scoutingCenter.decisiveActions} />
      </LabelM>
      {mainDecisiveActions.map(({ value, icon, label }) => (
        <Switch
          key={value}
          label={
            <Horizontal>
              {icon} <CapitalizeFirstLetter>{label}</CapitalizeFirstLetter>
            </Horizontal>
          }
          checked={stats.some(v => v === value)}
          onChange={() =>
            onChange(
              stats.includes(value)
                ? stats.filter(v => v !== value)
                : [...stats, value]
            )
          }
        />
      ))}
      <Vertical gap={showMoreDecisive ? 1 : 0}>
        <Collapse open={showMoreDecisive} gap={0}>
          <Vertical>
            {otherDecisiveActions.map(({ value, icon, label }) => (
              <Switch
                key={value}
                label={
                  <Horizontal>
                    {icon}{' '}
                    <CapitalizeFirstLetter>{label}</CapitalizeFirstLetter>
                  </Horizontal>
                }
                checked={stats.some(v => v === value)}
                onChange={() =>
                  onChange(
                    stats.includes(value)
                      ? stats.filter(v => v !== value)
                      : [...stats, value]
                  )
                }
              />
            ))}
          </Vertical>
        </Collapse>
        <ButtonBase onClick={() => setShowMoreDecisive(!showMoreDecisive)}>
          <LabelS bold>
            <StyledChevronDownBold className={showMoreDecisive ? 'open' : ''} />
            <FormattedMessage
              {...(showMoreDecisive ? glossary.showLess : glossary.showMore)}
            />
          </LabelS>
        </ButtonBase>
      </Vertical>
    </Vertical>
  );
};
